import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PlanPublic } from '@plans/shared';
import { ApiError, plansApi } from '../api/client.js';
import { CopyButton } from '../components/CopyButton.js';
import { StatusBadge } from '../components/StatusBadge.js';

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('ar');
}

function PlanActions({
  plan,
  onRefresh,
  onError,
}: {
  plan: PlanPublic;
  onRefresh: () => Promise<void>;
  onError: (message: string) => void;
}) {
  return (
    <div className="action-bar">
      <div className="action-group">
        <Link className="btn" to={`/plans/${plan.id}`}>
          إدارة
        </Link>
        <a className="btn secondary" href={plan.publicUrl} target="_blank" rel="noreferrer">
          فتح
        </a>
        <CopyButton value={plan.publicUrl} />
        <button
          type="button"
          className="btn secondary"
          onClick={() => {
            void plansApi
              .patch(plan.id, { published: !plan.published })
              .then(onRefresh)
              .catch((err: unknown) => {
                onError(err instanceof ApiError ? err.message : 'تعذر تحديث النشر.');
              });
          }}
        >
          {plan.published ? 'إلغاء النشر' : 'نشر'}
        </button>
      </div>
      <button
        type="button"
        className="btn danger"
        onClick={() => {
          if (!window.confirm(`حذف «${plan.title}»؟ لا يمكن التراجع عن هذا الإجراء.`)) {
            return;
          }
          void plansApi
            .remove(plan.id)
            .then(onRefresh)
            .catch((err: unknown) => {
              onError(err instanceof ApiError ? err.message : 'تعذر حذف الخطة.');
            });
        }}
      >
        حذف
      </button>
    </div>
  );
}

export function PlansListPage() {
  const [plans, setPlans] = useState<PlanPublic[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    const data = await plansApi.list();
    setPlans(data.plans);
  }

  useEffect(() => {
    void refresh().catch((err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'تعذر تحميل الخطط.');
    });
  }, []);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>استراتيجيات التسويق</h1>
          <p className="muted">انشر مواقع الخطط وشارك روابطها العامة.</p>
        </div>
        <Link className="btn" to="/plans/new">
          خطة جديدة
        </Link>
      </div>
      {error && <p className="error">{error}</p>}
      <div className="card table-wrap desktop-table">
        {plans.length === 0 ? (
          <p className="muted">لا توجد خطط بعد. أنشئ خطة للبدء.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>العنوان</th>
                <th className="hide-sm">المسار</th>
                <th>الحالة</th>
                <th className="hide-sm">التحديث</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id}>
                  <td>
                    <strong>{plan.title}</strong>
                    <div className="muted" dir="ltr">
                      {plan.publicUrl}
                    </div>
                  </td>
                  <td className="mono hide-sm" dir="ltr">
                    {plan.publicPath ?? plan.id}
                  </td>
                  <td>
                    <div className="stack" style={{ gap: '0.35rem' }}>
                      <StatusBadge on={plan.published} onLabel="منشورة" offLabel="غير منشورة" />
                      <StatusBadge
                        on={plan.passwordProtected}
                        onLabel="محمية"
                        offLabel="مفتوحة"
                      />
                    </div>
                  </td>
                  <td className="hide-sm">
                    <div>أُنشئت {formatDate(plan.createdAt)}</div>
                    <div className="muted">الملفات {formatDate(plan.filesUpdatedAt)}</div>
                  </td>
                  <td>
                    <PlanActions plan={plan} onRefresh={refresh} onError={setError} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="plan-cards">
        {plans.length === 0 ? (
          <div className="card">
            <p className="muted">لا توجد خطط بعد. أنشئ خطة للبدء.</p>
          </div>
        ) : (
          plans.map((plan) => (
            <article className="card stack" key={plan.id}>
              <div>
                <h2>{plan.title}</h2>
                <div className="mono muted" dir="ltr">
                  {plan.publicUrl}
                </div>
              </div>
              <div className="row">
                <StatusBadge on={plan.published} onLabel="منشورة" offLabel="غير منشورة" />
                <StatusBadge on={plan.passwordProtected} onLabel="محمية" offLabel="مفتوحة" />
              </div>
              <p className="muted">
                أُنشئت {formatDate(plan.createdAt)}
                <br />
                الملفات {formatDate(plan.filesUpdatedAt)}
              </p>
              <PlanActions plan={plan} onRefresh={refresh} onError={setError} />
            </article>
          ))
        )}
      </div>
    </div>
  );
}
